export const slugify = (text) => {
  // Convert the text to lowercase
  const normalizedText = text.toString().toLowerCase()

  // Replace accented characters with their non-accented equivalents
  const slugifiedText = normalizedText
    // Normalize accents: decompose accented characters into base characters and combining diacritical marks
    .normalize('NFD')
    // Remove remaining diacritical marks
    .replace(/[\u0300-\u036f]/g, '')
    // Remove non-alphanumeric, non-space, and non-dash characters
    .replace(/[^\w\s-]/g, '')
    // Replace spaces with dashes
    .replace(/\s+/g, '-')
    // Replace sequences of spaces or dashes with a single dash
    .replace(/[-\s]+/g, '-')
    // Trim spaces from the beginning and end of the string
    .replace(/^\s+|\s+$/g, '')

  return slugifiedText
}

export const getPoiLabel = (poi, activeLanguage) => {
  if (poi) {
    let poiLabel =
      activeLanguage === 'en'
        ? poi.labelEn
        : activeLanguage === 'es'
        ? poi.labelEs
        : poi.labelFr

    return poiLabel ? poiLabel : ''
  }
  return ''
}

export const getPoiFullLabel = (poi, activeLanguage) => {
  if (poi) {
    const poiTypeLabel = getPoiTypeLabelFromPoi(poi, activeLanguage)
    const poiLabel = getPoiLabel(poi, activeLanguage)
    return `${poiTypeLabel} - ${poiLabel} - ${poi.id}`
  }
  return ''
}

export const getPoiTypeLabelFromPoi = (poi, activeLanguage) => {
  if (poi && poi.poiType) {
    return activeLanguage === 'en'
      ? poi.poiType.labelEn
      : activeLanguage === 'es'
      ? poi.poiType.labelEs
      : poi.poiType.labelFr
  }
  return ''
}

/**
 * Recherche le POI de type "VILLAGE" correspondant à la même ville que le POI passé en argument.
 * @param {Object} poi          Le POI de référence.
 * @param {Array<Object>} pois  La liste de tous les POIs.
 * @returns {Object|undefined}  Le POI village, ou undefined s’il n’est pas trouvé.
 */
export const getPoiVillage = (poi, pois) => {
  if (!poi || !poi.city || !pois) return undefined

  return pois.find(
    (other) =>
      other.poiType?.name === 'VILLAGE' && other.city?.id === poi.city.id,
  )
}

export const getPoiTypeLabel = (poiTypes, typeName, activeLanguage) => {
  const poiType = poiTypes.find((type) => type.name === typeName)
  if (poiType) {
    return activeLanguage === 'en'
      ? poiType.labelEn
      : activeLanguage === 'es'
      ? poiType.labelEs
      : poiType.labelFr
  }
  return ''
}
export const getParameterValue = (poi, key) => {
  const parameter = poi.poiParameters.find(
    (parameter) => parameter.poiKey === key,
  )
  return parameter ? parameter.poiValue : null
}

export const getEquivalentKm = (poi) => {
  return getParameterValue(poi, 'equivalentKm')
}

export const getLevel = (poi) => {
  return getParameterValue(poi, 'level')
}

export const getAltitude = (poi) => {
  return getParameterValue(poi, 'altitude')
}

export const getType = (poi) => {
  return getParameterValue(poi, 'type')
}

export const getDistance = (poi, isKm = false) => {
  const distance = getParameterValue(poi, 'distance')
  if (distance !== null && isKm) {
    return (parseFloat(distance) / 1000).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })
  }
  return distance
}

export const getTechnicity = (poi) => {
  return getParameterValue(poi, 'technicity')
}

export const getEmail = (poi) => {
  return getParameterValue(poi, 'email')
}

export const getPhone = (poi) => {
  return getParameterValue(poi, 'phone')
}

export const getSurface = (poi) => {
  return getParameterValue(poi, 'surface')
}

export const getSurfaceType = (poi) => {
  return getParameterValue(poi, 'surfaceType')
}

export const getElevationGain = (poi) => {
  return getParameterValue(poi, 'elevationGain')
}

export const getElevationLoss = (poi) => {
  return getParameterValue(poi, 'elevationLoss')
}

export const getPoiColor = (poi) => {
  const level = getLevel(poi)
  switch (level) {
    case 'GREEN':
      return '1,110,1'
    case 'BLUE':
      return '1,1,130'
    case 'RED':
      return '150,1,1'
    case 'BLACK':
      return '20,20,20'
    default:
      return '46, 82, 49'
  }
}

export const getPoiMarkerPath = (poi) => {
  const level = getLevel(poi)
  return level
    ? `/images/markers/marker_50_${level}_${poi.numero}.png`
    : `/images/markers/marker_50_DEFAULT_${poi.numero}.png`
}

export const calculateZoom = (latMax, latMin, lngMax, lngMin) => {
  const zoomConfig = [
    { level: 9, maxDeltaLat: 1.25, maxDeltaLng: 1.5625 },
    { level: 10, maxDeltaLat: 0.625, maxDeltaLng: 0.78125 },
    { level: 11, maxDeltaLat: 0.3125, maxDeltaLng: 0.390625 },
    { level: 12, maxDeltaLat: 0.13, maxDeltaLng: 0.1953125 },
    { level: 13, maxDeltaLat: 0.05, maxDeltaLng: 0.09765625 },
    { level: 14, maxDeltaLat: 0.01953125, maxDeltaLng: 0.0244140625 },
    { level: 15, maxDeltaLat: 0.009765625, maxDeltaLng: 0.01220703125 },
  ]

  const latFraction = Math.abs(latMax - latMin)
  const lngFraction = Math.abs(lngMax - lngMin)

  let zoomLevel = 0
  for (let i = 0; i < zoomConfig.length; i++) {
    const config = zoomConfig[i]
    if (
      latFraction <= config.maxDeltaLat &&
      lngFraction <= config.maxDeltaLng
    ) {
      zoomLevel = config.level
    } else {
      break
    }
  }

  return zoomLevel
}

export const getGpsxMax = (pois, hasPolyline = true) => {
  if (!pois || pois.length === 0) return null
  if (hasPolyline) {
    return Math.max(...pois.map((poi) => poi.gpsxMax))
  } else {
    return Math.max(...pois.map((poi) => poi.gpsx))
  }
}

export const getGpsyMax = (pois, hasPolyline = true) => {
  if (!pois || pois.length === 0) return null
  if (hasPolyline) {
    return Math.max(...pois.map((poi) => poi.gpsyMax))
  } else {
    return Math.max(...pois.map((poi) => poi.gpsy))
  }
}

export const getGpsxMin = (pois, hasPolyline = true) => {
  if (!pois || pois.length === 0) return null
  if (hasPolyline) {
    return Math.min(...pois.map((poi) => poi.gpsxMin))
  } else {
    return Math.min(...pois.map((poi) => poi.gpsx))
  }
}

export const getGpsyMin = (pois, hasPolyline = true) => {
  if (!pois || pois.length === 0) return null
  if (hasPolyline) {
    return Math.min(...pois.map((poi) => poi.gpsyMin))
  } else {
    return Math.min(...pois.map((poi) => poi.gpsy))
  }
}
